import { z } from 'zod';
import { dateOnly, id, nonEmpty, optionalText, url } from './common';

/**
 * MarketingBee wire schemas: Brand Brain, plans, content items, channels, links and email
 * audiences. Enum lists live here so the DB, domain logic, API and web share one copy.
 */

export const CHANNELS = ['instagram', 'facebook', 'linkedin', 'bluesky', 'email', 'blog'] as const;
export const channel = z.enum(CHANNELS);
export type Channel = z.infer<typeof channel>;

export const CHANNEL_LABELS: Record<Channel, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  bluesky: 'Bluesky',
  email: 'Email',
  blog: 'Blog',
};

export const CONTENT_STATUSES = ['draft', 'in_review', 'changes_requested', 'approved', 'scheduled', 'publishing', 'published', 'failed', 'rejected', 'archived'] as const;
export const contentStatus = z.enum(CONTENT_STATUSES);
export type ContentStatus = z.infer<typeof contentStatus>;

export const CONTENT_ACTIONS = ['edit', 'submit', 'approve', 'request_changes', 'reject', 'schedule', 'unschedule', 'start_publish', 'publish_ok', 'publish_fail', 'retry', 'archive'] as const;
export const contentAction = z.enum(CONTENT_ACTIONS);
export type ContentAction = z.infer<typeof contentAction>;

/** What a person can do from the UI or the digest (the publish states belong to the cron). */
export const reviewAction = z.enum(['submit', 'approve', 'request_changes', 'reject', 'schedule', 'unschedule', 'retry', 'archive']);
export type ReviewAction = z.infer<typeof reviewAction>;

export const CONNECTION_KINDS = ['konekto_post', 'bluesky'] as const;
export const connectionKind = z.enum(CONNECTION_KINDS);
export type ConnectionKind = z.infer<typeof connectionKind>;

export const LANGUAGES = ['en', 'fr'] as const;
export const language = z.enum(LANGUAGES);

const shortId = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(48)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'Lowercase letters, numbers and dashes');
const phrase = z.string().trim().min(1).max(200);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM');

export const audienceSchema = z.object({ id: shortId, name: nonEmpty(120), description: z.string().trim().max(1000).default(''), pains: z.array(phrase).max(10).default([]) });
export const pillarSchema = z.object({
  id: shortId,
  name: nonEmpty(120),
  description: z.string().trim().max(1000).default(''),
  weight: z.number().int().min(0).max(10).default(1),
  humanOnly: z.boolean().default(false),
});
export const guardrailsSchema = z.object({
  bannedPhrases: z.array(phrase).max(100).default([]),
  avoidTopics: z.array(phrase).max(50).default([]),
  blockUnverifiedClaims: z.boolean().default(true),
  approvedClaims: z.array(phrase).max(50).default([]),
});
export const cadenceSchema = z.object({
  channel,
  postsPerWeek: z.number().int().min(0).max(21),
  times: z.array(hhmm).min(1).max(6),
  days: z.array(z.number().int().min(1).max(7)).min(1).max(7),
});

const timezone = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, 'Unknown time zone');

const uniqueIds = <T extends { id: string }>(list: T[]) => new Set(list.map((x) => x.id)).size === list.length;

export const brandProfileSchema = z.object({
  name: nonEmpty(120),
  website: url.nullable().default(null),
  description: z.string().trim().max(2000).default(''),
  voice: z.string().trim().max(500).default(''),
  timezone: timezone.default('America/Toronto'),
  languages: z.array(language).min(1).max(2).default(['en']),
  ctaUrl: url.nullable().default(null),
  /** Shown in every marketing email footer (CASL s.6(2), CAN-SPAM). Required before email sends. */
  mailingAddress: z.string().trim().max(300).default(''),
  audiences: z.array(audienceSchema).max(10).refine(uniqueIds, 'Audience ids must be unique').default([]),
  pillars: z.array(pillarSchema).max(12).refine(uniqueIds, 'Pillar ids must be unique').default([]),
  guardrails: guardrailsSchema.default({ bannedPhrases: [], avoidTopics: [], blockUnverifiedClaims: true, approvedClaims: [] }),
  cadence: z.array(cadenceSchema).max(CHANNELS.length).refine((c) => new Set(c.map((x) => x.channel)).size === c.length, 'One cadence per channel').default([]),
});
export type BrandProfileInput = z.input<typeof brandProfileSchema>;

export const createBrandInput = z.object({ profile: brandProfileSchema, industry: z.string().max(48).optional() });
export const updateBrandInput = z.object({ brandId: id, profile: brandProfileSchema });

export const planWeekInput = z.object({ brandId: id, weekStart: dateOnly.optional(), /** Re-draft slots that are still untouched drafts. */ redraft: z.boolean().default(false) });

export const contentListInput = z.object({
  brandId: id.optional(),
  statuses: z.array(contentStatus).max(CONTENT_STATUSES.length).optional(),
  channels: z.array(channel).max(CHANNELS.length).optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  limit: z.number().int().min(1).max(200).default(100),
});

export const createContentInput = z.object({
  brandId: id,
  channel,
  pillarId: shortId.nullable().default(null),
  audienceId: shortId.nullable().default(null),
  title: optionalText(200),
  body: z.string().max(40_000).default(''),
  scheduledAt: z.coerce.date().nullable().default(null),
  campaign: z.string().trim().max(80).nullable().default(null),
  /** Written by a person (always true for human-only pillars). */
  humanWritten: z.boolean().default(true),
});

export const updateContentInput = z.object({
  contentId: id,
  title: optionalText(200),
  body: z.string().max(40_000).optional(),
  hashtags: z.array(z.string().trim().max(60)).max(30).optional(),
  scheduledAt: z.coerce.date().nullable().optional(),
  campaign: z.string().trim().max(80).nullable().optional(),
  mediaIds: z.array(id).max(10).optional(),
});

export const contentActionInput = z.object({ contentId: id, action: reviewAction, comment: optionalText(2000) });
export const redraftInput = z.object({ contentId: id, direction: optionalText(500) });

export const connectInput = z.object({
  kind: connectionKind,
  label: z.string().trim().max(80).optional(),
  /** Field values by env-style name (CHANNEL_PROVIDERS[kind].fields); null clears, omitted keeps. */
  fields: z.record(z.string(), z.string().max(4000).nullable()),
});

export const createLinkInput = z.object({ brandId: id, destination: url, channel: channel.default('email'), campaign: z.string().trim().min(1).max(80), content: z.string().trim().max(80).optional() });

export const conversionInput = z.object({
  /** Short-link code or the `mb` click id we append to destinations. */
  clickId: z.string().trim().max(64).optional(),
  utmSource: z.string().trim().max(80).optional(),
  utmCampaign: z.string().trim().max(120).optional(),
  event: z.enum(['signup', 'activation', 'purchase', 'lead']).default('signup'),
  valueCents: z.number().int().nonnegative().optional(),
  externalId: z.string().trim().max(120).optional(),
  occurredAt: z.coerce.date().optional(),
});

export const subscribeInput = z.object({
  email: z.email().max(320),
  firstName: z.string().trim().max(80).optional(),
  /** Must be true; the exact wording shown is stored with the consent event. */
  consent: z.literal(true),
  source: z.string().trim().max(80).optional(),
});

export const analyticsInput = z.object({ brandId: id.optional(), from: z.coerce.date(), to: z.coerce.date() });
